import { Component, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { DateAdapter } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { Chart } from "angular-highcharts";
import { ExportToCsv, Options } from "export-to-csv";
import * as moment from "moment";
import { BatteryType, EventService } from "src/app/services/event.service";
import Swal from "sweetalert2";
import { EpackDashboardTableComponent } from "./epack-dashboard-table/epack-dashboard-table.component";
import { PicoloEpackDashboardTableComponent } from "./picolo-epack-dashboard-table/picolo-epack-dashboard-table.component";
import { HvService } from "../../services/hv.service";
import { HvEpackDashboardTableComponent } from "./hv-epack-dashboard-table/hv-epack-dashboard-table.component";

interface FileData {
  user: string;
  metaData: MetaData;
}

interface MetaData {
  test_date: string;
  timestamp: number;
  battery_unique_key: number;
  bms_id: number;
  fileUrl: string;
  fileName: string;
}

interface EpackEolDataWithTestRig {
  test_rig_number: string;
  test_id: string;
  pack_id: string;
  test_stage: number;
  test_status: string;
  user_id: string;
  fileData: FileData;
  bms_id: string;
  created_at: string;
  updated_at: string;
  test_end_time: string;
  final_eol_result: string;
}

interface EpackEolResponseData {
  test_id: string;
  metaData: {
    test_date: Date;
    battery_id: string | number;
    bms_id: string | number;
    fileUrl: string;
    fileName: string;
  };
}

interface EpackEolTableData {
  "S.No": number;
  "Test Status": string;
  "Rig No"?: string;
  "Test ID": string;
  "Start Date": string;
  "End Date"?: string;
  "Battery ID": string | number;
  "BMS ID": string | number;
  fileName: string;
  "Test Stage"?: string;
  final_eol_result?: string;
}

export const chartConfigs = {
  colors: {
    completed: "#1d9974",
    pending: "#d93f44",
    total: "#65a0e0",
  },
  years: [],
  axisData: {
    monthlyTests: [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ],
  },
};

@Component({
  selector: "app-exponent-epack",
  templateUrl: "./epack.component.html",
  styleUrls: ["./epack.component.css"],
})
export class ExponentEpackComponent implements OnInit {
  constructor(
    private hvService: HvService,
    private eventService: EventService,
    public dialog: MatDialog,
    private dateAdapter: DateAdapter<Date>
  ) {
    this.dateAdapter.setLocale("en-GB");
  }

  @ViewChild(EpackDashboardTableComponent)
  epackDashboardTableComponent: EpackDashboardTableComponent;

  @ViewChild(PicoloEpackDashboardTableComponent)
  picoloepackDashboardTableComponent: PicoloEpackDashboardTableComponent;

  @ViewChild(HvEpackDashboardTableComponent)
  hvEpackDashboardTableComponent: HvEpackDashboardTableComponent;

  isDataloading: boolean = false;
  epackTableResponseData: EpackEolResponseData[] = [];
  epackEolTableData: MatTableDataSource<EpackEolTableData> =
    new MatTableDataSource<EpackEolTableData>([]);
  monthsByIndex: { [index: number]: string } = {
    1: "Jan",
    2: "Feb",
    3: "Mar",
    4: "Apr",
    5: "May",
    6: "Jun",
    7: "Jul",
    8: "Aug",
    9: "Sep",
    10: "Oct",
    11: "Nov",
    12: "Dec",
  };
  chartYear: number = new Date().getFullYear();
  chartMonth: number = new Date().getMonth() + 1;
  defaultChartOptions: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    legend: {
      enabled: true,
    },
    yAxis: {
      title: {
        text: "",
      },
    },
  };
  graphHeight: number = 300;
  yearChart: Chart;
  monthChart: Chart;
  yearChartData: {
    [year: number]: number[];
  };
  monthChartData: Record<
    BatteryType,
    {
      completed: Record<string, number[]>;
      pending: Record<string, number[]>;
    }
  >;
  selectedTab: BatteryType = "tg";

  // testFailedBgColor: string = "#b84d4d";
  dateRange: FormGroup = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  });
  getTableDataOnInterval: boolean = false;
  intervalId: any;

  ngOnInit(): void {
    this.getYearlyTestsGraphData();
    this.getMonthlyTestsGraphData();
  }

  getEpackEolData() {
    this.epackDashboardTableComponent.getEpackEolDataByTestRig();
    this.picoloepackDashboardTableComponent.getEpackEolDataByTestRig();
    this.hvEpackDashboardTableComponent.getEpackEolDataByTestRig();
  }

  onYearSelectionChange() {
    this.plotYearlyTestsGraph(
      this.yearChartData[this.selectedTab][this.chartYear]
    );
    this.getMonthlyTestsGraphData();
    this.chartMonth = new Date().getMonth() + 1;
  }

  renderSelectedMonthTestsGraph() {
    this.plotMonthlyTestsGraph({
      completedTetsData:
        this.monthChartData[this.selectedTab].completed[this.chartMonth],
      pendingTestsData:
        this.monthChartData[this.selectedTab].pending[this.chartMonth],
    });
  }

  getEpackTestsByDateRangeWithRigData() {
    if (!this.dateRange.value.start || !this.dateRange.value.end) {
      Swal.fire({
        title: "Error!",
        icon: "error",
        text: "Invalid date selection",
      });
    } else {
      const startDate = moment(this.dateRange.value.start).format("YYYY-MM-DD");
      const endDate = moment(this.dateRange.value.end).format("YYYY-MM-DD");

      const payload = {
        startDate,
        endDate,
      };

      this.epackTableResponseData = [];
      this.eventService.getEpackTestsByDateRangeWithRigData(payload).subscribe(
        (res: { success: boolean; data: EpackEolDataWithTestRig[] }) => {
          if (res.success === true) {
            this.epackDashboardTableComponent.setTableData(res.data);
          }
        },
        (error) => {
          console.log("error getting epack eol data by date range ");
        }
      );

      this.eventService
        .getPicoloEpackTestsByDateRangeWithRigData(payload)
        .subscribe(
          (res: { success: boolean; data: EpackEolDataWithTestRig[] }) => {
            if (res.success === true) {
              this.picoloepackDashboardTableComponent.setTableData(res.data);
            }
          },
          (error) => {
            console.log("error getting epack eol data by date range ");
          }
        );

      this.hvService.getHvEpackTestsByDateRangeWithRigData(payload).subscribe(
        (res: { success: boolean; data: EpackEolDataWithTestRig[] }) => {
          if (res.success === true) {
            this.hvEpackDashboardTableComponent.setTableData(res.data);
          }
        },
        (error) => {
          console.log("error getting epack eol data by date range ");
        }
      );
    }
  }

  onDateRangeReset() {
    this.dateRange.reset();
    this.getEpackEolData();
  }

  toggleGolive() {
    this.getTableDataOnInterval = !this.getTableDataOnInterval;
    if (this.getTableDataOnInterval) {
      this.getEpackEolData();
      this.intervalId = setInterval(() => {
        this.getEpackEolData();
      }, 1000 * 60 * 2);
    } else {
      clearInterval(this.intervalId);
    }
  }

  downloadCsvReport() {
    Swal.fire({
      title: "Choose Battery Type",
      text: "Please select one of the following options:",
      icon: "question",
      html: `
    <div style="display: flex; flex-direction: column; gap: 10px; margin-top: 2rem">
      <button id="btn-tg" class="btn btn-primary">TG</button>
      <button id="btn-pl" class="btn btn-primary">Piccolo</button>
      <button id="btn-hv" class="btn btn-primary">HV</button>
    </div>
  `,
      showConfirmButton: false,
      didOpen: () => {
        const tgBtn = document.getElementById("btn-tg");
        const plBtn = document.getElementById("btn-pl");
        const hvBtn = document.getElementById("btn-hv");

        tgBtn?.addEventListener("click", () => {
          Swal.close({ value: "tg" } as any);
        });

        plBtn?.addEventListener("click", () => {
          Swal.close({ value: "piccolo" } as any);
        });

        hvBtn?.addEventListener("click", () => {
          Swal.close({ value: "hv" } as any);
        });
      },
    }).then((res) => {
      const { value: battery_type } = res;
      if (battery_type !== undefined) {
        this.eventService.getCsvReportMetaData(battery_type).subscribe(
          (res: {
            success: boolean;
            data: {
              test_id: string;
              Date: string;
              [index: string]: string | number | null;
            }[];
          }) => {
            if (res.success) {
              const csvColumns: string[] = [];

              res.data.forEach((data) => {
                Object.keys(data).forEach((column) => {
                  if (!csvColumns.includes(column)) {
                    csvColumns.push(column);
                  }
                });
              });

              const csvData: {
                [index: string]: string | number;
              }[] = [];

              const dataTableByBatteryPack: Record<
                BatteryType,
                MatTableDataSource<EpackEolTableData>
              > = {
                tg: this.epackDashboardTableComponent.epackEolTableData,
                piccolo:
                  this.picoloepackDashboardTableComponent
                    .picoloEpackEolTableData,
                hv: this.hvEpackDashboardTableComponent.hvEpackEolTableData,
              };

              const dataItem = dataTableByBatteryPack[battery_type];

              const tableTestIds = dataItem.filteredData.map(
                (_) => _["Test ID"]
              );

              const validData = res.data.filter((_) =>
                tableTestIds.includes(_.test_id)
              );

              for (let i = 0; i < validData.length; i++) {
                const result: {
                  test_id?: string;
                  Date?: string;
                  [index: string]: string | number | null;
                } = {};

                const data = validData[i];
                const currentDataKeys = Object.keys(data);
                csvColumns.forEach((key) => {
                  if (!currentDataKeys.includes(key)) {
                    if (key === "Date") {
                      result["Date (DD-MM-YYYY HH:mm)"] = "----";
                    } else {
                      result[key] = "----";
                    }
                  } else {
                    if (key.toLowerCase().trim() === "date") {
                      result["Date (DD-MM-YYYY HH:mm)"] = data[key]
                        ? data[key]
                        : "---";
                    } else {
                      result[key] = data[key] !== null ? data[key] : "---";
                    }
                  }
                });

                const { test_id, rig_number, final_eol_result } = result;

                csvData.push({
                  test_id,
                  rig_number,
                  ["Date (DD-MM-YYYY HH:mm)"]:
                    result["Date (DD-MM-YYYY HH:mm)"],
                  final_eol_result,
                  ...result,
                });
              }

              const exportOptions: Options = {
                useKeysAsHeaders: true,
                useTextFile: false,
                filename: `${battery_type}_exponent_epack_${moment(
                  new Date()
                ).format("YYYY-MM-DD")}`,
              };

              const csvExporter = new ExportToCsv(exportOptions);
              csvExporter.generateCsv(csvData);
            }
          },
          (err) => {
            console.log("Error getting csv report meta data: ");
          }
        );
      }
    });
  }

  /* Component Methods */
  getMonthChartCategory(): string[] {
    const daysInMonth = new Date(this.chartYear, this.chartMonth, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, index) => String(index + 1));
  }
  getYearlyTestsGraphData() {
    this.eventService.getYearlyTestsGraphData().subscribe(
      (res: { success: boolean; data: { [index: number]: number[] } }) => {
        if (res.success) {
          this.yearChartData = res.data;
          this.plotYearlyTestsGraph(this.yearChartData["tg"][this.chartYear]);
        }
      },
      (err) => {
        console.log("error getting yearly tests graph data");
      }
    );
  }

  getMonthlyTestsGraphData() {
    this.eventService.getMonthlyTestsGraphData(this.chartYear).subscribe(
      (res) => {
        if (res.success) {
          this.monthChartData = res.data;

          this.plotMonthlyTestsGraph({
            completedTetsData:
              this.monthChartData[this.selectedTab].completed[this.chartMonth],
            pendingTestsData:
              this.monthChartData[this.selectedTab].pending[this.chartMonth],
          });
        }
      },
      (err) => {
        console.log("error getting monthly tests graph data");
      }
    );
  }

  plotYearlyTestsGraph(graphData: number[]) {
    const yearChartOptions: Highcharts.Options = {
      ...this.defaultChartOptions,
      legend: {
        enabled: false,
      },
      chart: {
        type: "column",
        height: this.graphHeight,
      },
      tooltip: {
        headerFormat: '<h1 style=""><b>{point.key}:</b> {point.y}</h1></br>',
        pointFormat: "",
        footerFormat: "",
      },
      xAxis: { categories: chartConfigs.axisData.monthlyTests },
      series: [
        {
          type: "column",
          color: chartConfigs.colors.total,
          data: graphData ?? Array(12).fill(0),
        },
      ],
    };

    this.yearChart = new Chart(yearChartOptions);
  }

  plotMonthlyTestsGraph(data: {
    completedTetsData: number[];
    pendingTestsData: number[];
  }) {
    const monthChartOptions: Highcharts.Options = {
      ...this.defaultChartOptions,
      chart: {
        type: "line",
        height: this.graphHeight,
      },
      xAxis: {
        categories: this.getMonthChartCategory(),
      },
      tooltip: {
        headerFormat: `<h2 style="">
        <b>${this.monthsByIndex[this.chartMonth]} {point.key} </b>
        </h2></br>`,
        shared: true,
      },
      series: [
        {
          type: "spline",
          name: "pending tests",
          color: chartConfigs.colors.pending,
          data: data.pendingTestsData,
        },
        {
          type: "spline",
          name: "completed tests",
          color: chartConfigs.colors.completed,
          data: data.completedTetsData,
        },
      ],
    };

    this.monthChart = new Chart(monthChartOptions);
  }

  onIndexChange(clickedTab: number) {
    const tabs: BatteryType[] = ["tg", "piccolo", "hv"];
    this.selectedTab = tabs[clickedTab] ?? this.selectedTab;

    this.plotMonthlyTestsGraph({
      completedTetsData:
        this.monthChartData[this.selectedTab].completed[this.chartMonth],
      pendingTestsData:
        this.monthChartData[this.selectedTab].pending[this.chartMonth],
    });

    this.plotYearlyTestsGraph(
      this.yearChartData[this.selectedTab][this.chartYear]
    );
  }
}
